/**
 * filesHelpers — Pure helpers for the file search results page.
 */
import { ExecutionResponse } from 'src/store/slices/execution/executionState';

/** A searched file name and the executions whose executionTags matched it. */
export interface FileExecutionGroup {
  fileName: string;
  executions: ExecutionResponse[];
}

/** Removes duplicate names while preserving first-seen order. */
export const dedupeNames = (names: string[]): string[] => Array.from(new Set(names));

/**
 * Groups executions under each searched file name via executionTags.
 * An execution appears under every name it matched. Names with no
 * matching execution are omitted (they surface via the notFound alert).
 */
export const groupExecutionsByFileName = (
  fileNames: string[],
  executions: ExecutionResponse[],
): FileExecutionGroup[] =>
  fileNames
    .map((fileName) => ({
      fileName,
      executions: executions.filter((e) => e.executionTags?.includes(fileName) ?? false),
    }))
    .filter((group) => group.executions.length > 0);

/** One table row per (searched file name, matching execution) pair. */
export interface FileExecutionRow {
  rowKey: string;
  fileName: string;
  execution: ExecutionResponse;
}

/**
 * Flattens file-name groups into table rows. A file that matched several
 * executions produces one row per execution; an execution matching several
 * searched names appears once per name.
 */
export const buildFileExecutionRows = (
  fileNames: string[],
  executions: ExecutionResponse[],
): FileExecutionRow[] => {
  const rows: FileExecutionRow[] = [];
  groupExecutionsByFileName(fileNames, executions).forEach((group) => {
    group.executions.forEach((execution) => {
      rows.push({
        rowKey: `${group.fileName}::${execution.id}`,
        fileName: group.fileName,
        execution,
      });
    });
  });
  return rows;
};
